import { useCallback, useState } from "react";

import { extractMessageLinks } from "modules/messages/links/extractMessageLinks";
import { useOpenLink } from "modules/messages/links/useOpenLink";

import { PushNotificationActionPerformed } from "../IPushNotificationAdapter";
import { extractNotificationUrl } from "../utils/extractNotificationUrl";

export type PushMessage = {
  title?: string;
  body?: string;
  links: string[];
};

export const useDeepLinkNavigation = () => {
  const { openLink } = useOpenLink();
  const [pushMessage, setPushMessage] = useState<PushMessage | null>(null);

  const handleNotificationAction = useCallback(
    async (actionPerformed: PushNotificationActionPerformed<unknown>) => {
      const { notification } = actionPerformed;

      console.log("[RMD]: handleNotificationAction", actionPerformed);

      // A new tap supersedes any message still on screen from an earlier one.
      setPushMessage(null);

      const url = extractNotificationUrl(notification);

      if (!url) {
        return;
      }

      // A notification can target a practice the patient is not currently acting as — an arrival
      // nudge fires for whichever practice they walked into — or an external page. The opener
      // switches practice first so the screen loads against that practice's API host and branding.
      // A web link is held back so the patient reads the message before choosing to leave the app.
      const classified = await openLink(url, { deferExternal: true });

      if (classified.kind === "external") {
        const bodyLinks = extractMessageLinks(notification.body ?? "");
        setPushMessage({
          title: notification.title,
          body: notification.body,
          links: [url, ...bodyLinks.filter((link) => link !== url)],
        });
      }
    },
    [openLink],
  );

  const dismissPushMessage = useCallback(() => setPushMessage(null), []);

  return { handleNotificationAction, pushMessage, dismissPushMessage };
};
